import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  getTokenResult,
  removePartnerId,
  removeRedirectUrl,
  removeToken,
  removeTokenResult,
  removeUserId,
  savePartnerId,
  saveRedirectUrl,
  saveToken,
  saveTokenResult,
  saveUserId,
} from "../lib/localStorage";
import { useLoading } from "./useLoading";
import { TokenResult } from "../../app/routing/domain/entities/Main";
import { setPartnerCookie } from "../../app/routing/hooks/useCookie";

const windowProtocol = window.location.protocol;
const windowHost = window.location.host;
const windowUrl = window.location.search;
const params = new URLSearchParams(windowUrl);

type AuthContextValue = {
  setToken: (token: TokenResult | null | undefined) => void;
  token?: TokenResult | null | undefined;
  setExpired: (isExpired: boolean) => void;
};

export const AuthContext = createContext<AuthContextValue>({
  setToken: () => {},
  token: null,
  setExpired: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const { showLoading } = useLoading();
  const [token, setTokenState] = useState<TokenResult | null | undefined>();
  const currentTokenResult = getTokenResult();

  const setExpired = (isExpired: boolean) => {
    if (isExpired) {
      setTokenState(null);
      removeUserId();
      removePartnerId();
      removeToken();
      removeTokenResult();
      removeRedirectUrl();
    }
  };

  function setToken(tokenResult: TokenResult | null | undefined) {
    setTokenState(tokenResult);
    saveRedirectUrl(windowProtocol + "//" + windowHost + windowUrl);
    saveToken(tokenResult?.token ?? "");
    saveTokenResult(JSON.stringify(tokenResult) ?? "");
    savePartnerId(tokenResult?.partnerId ?? "");
    saveUserId(
      tokenResult?.userId
        ? tokenResult?.userId
        : tokenResult?.mobilePhone
        ? tokenResult.mobilePhone
        : ""
    );
  }

  useEffect(() => {
    if (currentTokenResult) {
      if (token === undefined) {
        setToken(JSON.parse(currentTokenResult));
      }
    } else {
      showLoading(true, 750);
    }
  }, [currentTokenResult]);

  return (
    <AuthContext.Provider
      value={{
        setToken,
        token,
        setExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const { setToken, token, setExpired } = useContext(AuthContext);

  return { setToken, token, setExpired };
}
